import { useEffect, useRef } from 'react';

type SurveyedEntry = { description: string; cube: number };
type DetectionFrame = { timestamp: number; surveyed?: SurveyedEntry[] };

export const DETECTION_SURVEYED_CHANNEL = 'DETECTION_SURVEYED_ITEMS';
export type SurveyedItemCounts = Record<string, number>;

// Runs in the video popup window. As the video plays, fires each frame's
// surveyed items to the opener (main window) when their timestamp is reached.
// Stops after one full loop.
export function useDetectionAutoSurvey(
  videoContainerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean
) {
  const doneRef = useRef(false);
  const framesRef = useRef<DetectionFrame[]>([]);
  const lastAppliedIndexRef = useRef(-1);
  const rafRef = useRef(0);
  const prevTimeRef = useRef(0);

  // Reset scan state when detection is toggled off
  useEffect(() => {
    if (!enabled) {
      doneRef.current = false;
      lastAppliedIndexRef.current = -1;
      prevTimeRef.current = 0;
    }
  }, [enabled]);

  // Load frame data once detection is enabled
  useEffect(() => {
    if (!enabled) return;
    fetch('/detections.json')
      .then((r) => r.json())
      .then((frames: DetectionFrame[]) => {
        framesRef.current = frames;
      })
      .catch(() => {});
  }, [enabled]);

  // RAF loop: as video plays, fire each frame's surveyed items when its
  // timestamp is reached. Stops after detecting one full loop.
  useEffect(() => {
    if (!enabled) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      return;
    }

    const tick = () => {
      const video = videoContainerRef.current?.querySelector('video');
      if (video && !doneRef.current && !isNaN(video.duration)) {
        const curr = video.currentTime;
        const prev = prevTimeRef.current;

        // Detect loop: time was past halfway and has wrapped back to near start
        if (prev > video.duration * 0.5 && curr < 1.0) {
          doneRef.current = true;
        } else {
          const frames = framesRef.current;
          const target = window.opener ?? window;
          const origin = window.location.origin;

          for (
            let i = lastAppliedIndexRef.current + 1;
            i < frames.length;
            i++
          ) {
            const frame = frames[i];
            if (frame.timestamp > curr) break;

            const surveyed = frame.surveyed ?? [];
            if (surveyed.length > 0) {
              const payload: SurveyedItemCounts = {};
              for (const s of surveyed) {
                if (s.description) {
                  payload[s.description] = (payload[s.description] ?? 0) + 1;
                }
              }
              target.postMessage(
                { type: DETECTION_SURVEYED_CHANNEL, items: payload },
                origin
              );
            }
            lastAppliedIndexRef.current = i;
          }
        }

        prevTimeRef.current = curr;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [enabled, videoContainerRef]);
}
